import NumberFormat from 'react-number-format';
import { getHighestBid } from '../../utils/vehicle';
import styled from 'styled-components';
import Tooltip from '../Tooltip';
import { useRef, useState } from 'react';
import { useIsTextOverflown } from '../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import { loginAsUser } from '../../actions';
import { HoverInfoTooltip } from '../../pages/Admin/AuctionDetail/components/HoverInfo';
import { useAdminOfferAuction, useCustomerHoverStateHandlers } from '../../pages/Admin/AuctionDetail/hooks';
import { convertToLocalTime } from '../../utils';
import { ButtonBase, Icon, Popover } from '@material-ui/core';
import { ViewAllBidsTooltip } from '../../pages/Shared/VehicleDetails/components/BidHistorySection/components/ViewAllBidsModal';
import { AUCTION_TYPES } from 'constants';
import { VEHICLE_STATUSES } from 'constants/vehicleStatuses';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 14px;
`;

const BidderName = styled.span`
  display: inline-block;
  color: #757575;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 66px;
  cursor: pointer;
`;

const Label = styled.span`
  color: #757575;
  font-family: Roboto;
  font-size: 14px;
  font-weight: 400;
  max-width: 66px;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin-top: 8px;
`;

const StyledBackButton = styled(ButtonBase)`
  margin-top: -4px;
`;

const WinnerHighlight = styled.span`
  background: rgba(253, 183, 20, 0.3);
  border-radius: 2px;
  padding: 0 2px;
  font-weight: 500;
`;

const companyToLabel: Record<string, string> = {
  acv: 'ACV',
  other: 'Other',
  open_lane: 'OL'
};

export const HighestBidWithBuyer = ({ vehicle, showHoverInfo = false }: any) => {
  const highestBid = getHighestBid(vehicle);
  const offerAuction = useAdminOfferAuction(vehicle.id);
  const isAlternativeAuction = vehicle.auction && vehicle.auction.type !== AUCTION_TYPES.AUTOAXESS;
  const isAwarded = vehicle.status === VEHICLE_STATUSES.AWARDED;
  const hasOfferBids = (offerAuction?.offerBids?.length ?? 0) > 0;
  const showWinner = isAwarded && !isAlternativeAuction && !hasOfferBids;

  const buyerName = (() => {
    if (showWinner) return vehicle.awarded_user?.dealership_name || '';
    if (highestBid.bidder && highestBid.bidder.is_bot) {
      return `${companyToLabel[highestBid.bidder.company_name]} - ${highestBid.bidder.dealership_name}`;
    }
    return highestBid.bidder?.dealership_name;
  })();
  const admin = useSelector((state: any) => state?.user?.user);
  const isLister = admin?.job_title === 'Lister';

  const ref = useRef<HTMLSpanElement>(null);
  const isTextOverflown = useIsTextOverflown(ref);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpenPopover = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const vehicleBids = (vehicle.bids || []).map((bid: any) => ({
    id: bid.id,
    bid: bid.amount,
    bidder: bid.bidder?.dealership_name,
    date: convertToLocalTime(bid.created_at, true),
    created_at: bid.created_at,
    admin: bid.admin
  }));

  const dispatch = useDispatch();

  const handleClick = () => {
    if (highestBid.bidder?.is_bot || isLister) return;
    dispatch(loginAsUser({ id: highestBid.user_id, initialPage: `/admin/auctions/${vehicle.auction_id}` }).request);
  };

  const showMinBid = vehicle.auction && vehicle.auction.type !== AUCTION_TYPES.SECOND_CHANCE;

  return (
    <Wrapper>
      {showMinBid && (
        <span style={{ color: '#666666', fontSize: '12px' }}>
          <NumberFormat displayType="text" prefix="$" value={vehicle.starting_bid || 0} thousandSeparator />
        </span>
      )}
      <span style={{ fontWeight: 500 }}>
        {showWinner ? (
          <WinnerHighlight>
            <NumberFormat displayType="text" prefix="$" value={vehicle.vehicle_price || 0} thousandSeparator />
          </WinnerHighlight>
        ) : (
          <NumberFormat displayType="text" prefix="$" value={(highestBid && highestBid.amount) || 0} thousandSeparator />
        )}
      </span>
      {buyerName && showWinner && vehicle.awarded_user && (
        <WinnerHighlight>
          <RenderBuyerNameWithHoverInfo
            userId={vehicle.awarded_user.id}
            initialPage={`/admin/auctions/${vehicle.auction_id}`}
            buyerName={buyerName}
          />
        </WinnerHighlight>
      )}
      {buyerName && !showWinner && showHoverInfo && highestBid.bidder && (
        <RenderBuyerNameWithHoverInfo
          userId={highestBid.bidder.id}
          initialPage={`/admin/auctions/${vehicle.auction_id}`}
          buyerName={buyerName}
        />
      )}
      {buyerName && !showWinner && !showHoverInfo && (
        <Tooltip isTextOverflown={isTextOverflown} title={buyerName} arrow light>
          <BidderName ref={ref} onClick={handleClick}>
            {buyerName}
          </BidderName>
        </Tooltip>
      )}
      {(vehicleBids.length > 0 || (vehicle.adminVisibleBuyers?.length ?? 0) > 0) && (
        <>
          <StyledBackButton onClick={handleOpenPopover}>
            <Icon>expand_more</Icon>
          </StyledBackButton>
          <Popover
            id="bid-history-popover"
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClosePopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
            marginThreshold={150}
          >
            <ViewAllBidsTooltip
              bids={vehicleBids}
              vehicleId={vehicle.id}
              type={'bids'}
              editable
              proxyBid={vehicle.proxy_bid}
              visibleBuyers={vehicle.adminVisibleBuyers ?? []}
            />
          </Popover>
        </>
      )}
    </Wrapper>
  );
};

const RenderBuyerNameWithHoverInfo = ({
  userId,
  buyerName,
  initialPage
}: {
  userId: number;
  buyerName: string;
  initialPage: string;
}) => {
  const { isOpen, handleClose, handleOpen } = useCustomerHoverStateHandlers();

  const ref = useRef<HTMLSpanElement>(null);
  const isTextOverflown = useIsTextOverflown(ref);

  if (!buyerName) return null;

  return (
    <HoverInfoTooltip
      anchorEl={ref.current}
      initialPage={initialPage}
      userId={userId}
      isPopoverOpened={isOpen}
      closeTooltip={handleClose}
      showTooltip={isTextOverflown}
      tooltipName={buyerName}
    >
      <BidderName ref={ref} onClick={handleOpen}>
        {buyerName}
      </BidderName>
    </HoverInfoTooltip>
  );
};
